import React, { useState, useEffect, useRef } from "react";


import { Header, AdminPreview, ScheduledTasksWidget } from "../../components/modules";
import Context from "../../Context";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  orderBy,
  limit,
} from "firebase/firestore";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { useToast } from "../../components/ui";
import { getAdminOrderUrl } from "../../constants";
import {
  FiShoppingCart,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiExternalLink,
  FiEye,
  FiCheck,
  FiX,
  FiVolume2,
  FiVolumeX,
  FiShoppingBag,
  FiLogIn,
  FiRefreshCw,
  FiUsers,
} from "react-icons/fi";
import { RiAlarmWarningLine, RiDeleteBin7Line, RiAdminLine, RiStarFill } from "react-icons/ri";
import { ArrowRightLeft, ShieldAlert, Lock } from "lucide-react";
import { getFavoriteUsers, removeFavoriteUser } from "../../util/favoriteUtils";
import { executeApi } from "../../util/apiHelper";
import { TransferReviewModal } from "../orders/orderInfo/components/TransferReviewModal";
import { BatchAnonymizeModal } from "../users/BatchAnonymizeModal";
import { LastTwoDigitsStockPanel } from "../dashboard/components/LastTwoDigitsStockPanel";



// Animated Number helper for smooth transitions
const AnimatedNumber = ({ value = 0, className = "", prefix = "", suffix = "" }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    if (value !== displayValue) {
      setIsChanged(true);
      const start = Number(displayValue) || 0;
      const end = Number(value) || 0;
      const duration = 400;
      const startTime = performance.now();

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const current = Math.floor(start + (end - start) * easeOut);
        setDisplayValue(current);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setDisplayValue(end);
          setTimeout(() => setIsChanged(false), 200);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [value]);

  return (
    <span
      className={`transition-transform duration-300 inline-block font-mono ${isChanged ? "scale-105 filter drop-shadow-[0_0_8px_rgba(52,211,153,0.9)]" : ""
        } ${className}`}
    >
      {prefix}
      {Number(displayValue).toLocaleString()}
      {suffix}
    </span>
  );
};

export const AdminCenter = () => {
  const { db, configs, callApi, profileState } = Context();
  const { toast } = useToast();
  const [roundDate, setRoundDate] = useState(configs?.roundDate || "");
  const [stats, setStats] = useState(null);
  const [rawDashboardData, setRawDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);


  // Realtime Active Carts State
  const [liveCartCount, setLiveCartCount] = useState(null);
  const [activeCarts, setActiveCarts] = useState([]);
  const [clearingCartId, setClearingCartId] = useState(null);

  // Realtime Pending & Paid Orders
  const [pendingOrdersList, setPendingOrdersList] = useState([]);
  const [paidOrdersList, setPaidOrdersList] = useState([]);
  const [isPendingLoaded, setIsPendingLoaded] = useState(false);
  const [isPaidLoaded, setIsPaidLoaded] = useState(false);

  // Admin Alerts & Security Logs State
  const [adminAlerts, setAdminAlerts] = useState([]);
  const [complainsList, setComplainsList] = useState([]);
  const [alertFilter, setAlertFilter] = useState("all");
  const [selectedSlipModal, setSelectedSlipModal] = useState(null);
  const [isAlertSoundEnabled, setIsAlertSoundEnabled] = useState(true);

  // Favorite Users State & Sync
  const [favoriteUsers, setFavoriteUsers] = useState(() => getFavoriteUsers());
  const [proxyLoginId, setProxyLoginId] = useState(null);

  // Realtime Pending Order Transfer Requests
  const [pendingTransferRequests, setPendingTransferRequests] = useState([]);
  const [selectedTransferModal, setSelectedTransferModal] = useState(null);

  const currentRole = (profileState?.role || "").toLowerCase();
  const canAnonymize =
    currentRole === "dev" ||
    currentRole === "ceo" ||
    currentRole === "superadmin" ||
    Boolean(profileState?.superAdmin) ||
    Boolean(profileState?.superSuperAdmin);

  const [pendingAnonymizeCount, setPendingAnonymizeCount] = useState(0);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);

  const fetchPendingTransfers = async () => {
    try {
      const res = await callApi(
        "orders-getTransferRequests",
        { status: "pending", limit: 20 },
        { notShowLoading: true }
      );
      if (res?.status && Array.isArray(res.data)) {
        setPendingTransferRequests(res.data);
      }
    } catch (e) {
      console.warn("fetchPendingTransfers error:", e);
    }
  };

  const fetchPendingAnonymizeSummary = async () => {
    try {
      const res = await callApi(
        "users-getPendingAnonymizeSummary",
        {},
        { notShowLoading: true }
      );
      if (res?.status) {
        setPendingAnonymizeCount(res.pendingCount || 0);
      }
    } catch (e) {
      console.warn("fetchPendingAnonymizeSummary error:", e);
    }
  };

  useEffect(() => {
    fetchPendingTransfers();
    fetchPendingAnonymizeSummary();

    // Polling ทุก 3 นาที แทน 15 วินาที เพื่อประหยัด Quota และลดการยิง API
    const interval = setInterval(() => {
      fetchPendingTransfers();
      fetchPendingAnonymizeSummary();
    }, 3 * 60 * 1000);

    const handleVisibility = () => {
      if (!document.hidden) {
        fetchPendingTransfers();
        fetchPendingAnonymizeSummary();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);


  useEffect(() => {
    const syncFavorites = () => {
      setFavoriteUsers(getFavoriteUsers());
    };
    window.addEventListener("favorite_users_updated", syncFavorites);
    window.addEventListener("storage", syncFavorites);
    return () => {
      window.removeEventListener("favorite_users_updated", syncFavorites);
      window.removeEventListener("storage", syncFavorites);
    };
  }, []);




  const handleProxyLogin = async (e, userId) => {
    e.stopPropagation();
    setProxyLoginId(userId);
    try {
      const result = await callApi("users-addAdminToken", { userId });
      if (result?.adminToken) {
        window.open(getAdminOrderUrl(result.adminToken), "_blank");
      } else {
        toast({
          className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
          title: "ไม่สามารถสร้าง Admin Token ได้",
        });
      }
    } catch (err) {
      toast({
        className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
        title: "เกิดข้อผิดพลาดในการเข้าสู่ระบบแทนลูกค้า",
        description: err?.message || String(err),
      });
    } finally {
      setProxyLoginId(null);
    }
  };

  const handleRemoveFavorite = (e, userId, name) => {
    e.stopPropagation();
    removeFavoriteUser(userId);
    toast({
      className: "bg-slate-800 text-white font-semibold py-2 px-3 rounded-md",
      title: `นำ ${name || "ลูกค้า"} ออกจากรายการโปรดแล้ว`,
    });
  };

  // Quick Action Modal for Orders
  const [quickOrderSlip, setQuickOrderSlip] = useState(null);
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  // Refs for unsubscriptions
  const unsubAlertsRef = useRef(null);
  const unsubComplainsRef = useRef(null);
  const unsubPendingRef = useRef(null);
  const unsubPaidRef = useRef(null);
  const unsubCartRef = useRef(null);
  const unsubDocRef = useRef(null);
  const prevAlertCountRef = useRef(0);
  const isFirstComplainsLoadRef = useRef(true);
  const prevComplainIdsRef = useRef(new Set());

  // Realtime Order Live Notifications Stack
  const [orderNotifications, setOrderNotifications] = useState([]);
  const isFirstPendingLoadRef = useRef(true);
  const isFirstPaidLoadRef = useRef(true);
  const prevPendingIdsRef = useRef(new Set());
  const prevPaidIdsRef = useRef(new Set());

  // Play procedural audio beep for danger security alerts
  const playAlertSound = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.3);

      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch {
      // Audio error ignored
    }
  };

  // Play pleasant upward arpeggio chime for new order updates
  const playOrderChime = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const ctx = new AudioContext();
      const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
      notes.forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + idx * 0.09);

        gain.gain.setValueAtTime(0.2, ctx.currentTime + idx * 0.09);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + idx * 0.09 + 0.22);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(ctx.currentTime + idx * 0.09);
        osc.stop(ctx.currentTime + idx * 0.09 + 0.22);
      });
    } catch {
      // Audio error ignored
    }
  };

  const addOrderNotification = (noti) => {
    setOrderNotifications((prev) => [noti, ...prev.filter((n) => n.orderId !== noti.orderId).slice(0, 3)]);
    // Auto dismiss after 5 seconds
    setTimeout(() => {
      setOrderNotifications((prev) => prev.filter((n) => n.id !== noti.id));
    }, 5000);
  };

  const dismissNotification = (id) => {
    setOrderNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  // Sync initial roundDate from configs
  useEffect(() => {
    if (configs?.roundDate && !roundDate) {
      setRoundDate(configs.roundDate);
    }
  }, [configs?.roundDate, roundDate]);

  // Fetch initial dashboard stock metadata
  const fetchDashboardData = async (isManualSync = false) => {
    if (isManualSync) setSyncing(true);
    try {
      const activeRound = roundDate || configs?.roundDate;
      const res = await callApi("dashboard-getDashboard", {
        configs: { roundDate: activeRound },
      });
      if (res?.status && res?.data) {
        setRawDashboardData(res.data);
        setStats(res.data);
      }
    } catch (err) {
      console.warn("fetchDashboardData error:", err);
    } finally {
      setLoading(false);
      if (isManualSync) {
        setTimeout(() => setSyncing(false), 500);
      }
      setLastUpdated(new Date());
    }
  };

  useEffect(() => {
    if (roundDate) {
      fetchDashboardData();
    }
  }, [roundDate]);

  // Subscribe to real-time stats via Firestore onSnapshot
  useEffect(() => {
    if (!roundDate || !db) return;

    try {
      if (unsubDocRef.current) unsubDocRef.current();

      unsubDocRef.current = onSnapshot(
        doc(db, "Dashboard", roundDate),
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setRawDashboardData(data);
            setStats(data);
            setLastUpdated(new Date());
          }
        },
        (err) => {
          console.warn("Dashboard doc listener warning:", err?.message);
        }
      );

      return () => {
        if (unsubDocRef.current) unsubDocRef.current();
      };
    } catch (e) {
      console.warn("Failed to attach Dashboard doc listener:", e);
    }
  }, [roundDate, db]);

  // Subscribe to real-time Active Carts
  useEffect(() => {
    if (!db) return;

    try {
      if (unsubCartRef.current) unsubCartRef.current();

      const q = query(collection(db, "Carts"), where("size", ">", 0));
      unsubCartRef.current = onSnapshot(
        q,
        (snapshot) => {
          let count = 0;
          const cartList = [];
          snapshot.forEach((d) => {
            const cart = d.data();
            const size = Number(cart.size || 0);
            count += size;
            cartList.push({
              id: d.id,
              ...cart,
              size,
              createdAt: cart.createdAt || null,
              expireAt: cart.expireAt || null,
            });
          });
          setLiveCartCount(count);
          cartList.sort((a, b) => (b.expireAt || 0) - (a.expireAt || 0));
          setActiveCarts(cartList);
        },
        (err) => {
          console.warn("Carts listener warning:", err?.message);
        }
      );

      return () => {
        if (unsubCartRef.current) unsubCartRef.current();
      };
    } catch (e) {
      console.warn("Failed to attach Carts listener:", e);
    }
  }, [db]);

  // Subscribe to Pending & Paid Orders
  useEffect(() => {
    if (!roundDate || !db) return;

    try {
      const qPending = query(
        collection(db, "Orders"),
        where("roundDate", "==", roundDate),
        where("status", "==", "pending"),
        limit(50)
      );
      unsubPendingRef.current = onSnapshot(
        qPending,
        (snap) => {
          const list = snap.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              shortId: data.shortId || d.id.slice(-6),
              totalPrice: data.totalPrice || 0,
              amountLak: data.amountLak,
              currency: data.currency,
              size: data.size || data.totalQuantity || (data.lotteries ? data.lotteries.length : 0),
              createdAt: data.createdAt,
              expireAt: data.expireAt,
              phone: data.phone,
              slipUrl: data.slipUrl || data.slip || null,
              customerName: `${data.firstName || ""} ${data.lastName || ""}`.trim() || data.phone || data.userId,
              status: data.status,
            };
          });
          list.sort((a, b) => (a.expireAt || 0) - (b.expireAt || 0));

          // Detect new pending orders
          if (!isFirstPendingLoadRef.current) {
            const newOrders = list.filter((o) => !prevPendingIdsRef.current.has(o.id));
            if (newOrders.length > 0) {
              if (isAlertSoundEnabled) playOrderChime();
              newOrders.forEach((ord) => {
                addOrderNotification({
                  id: `${ord.id}-${Date.now()}`,
                  orderId: ord.id,
                  shortId: ord.shortId,
                  type: "PENDING",
                  title: "คำสั่งซื้อใหม่ (รอชำระ)",
                  customerName: ord.customerName,
                  phone: ord.phone,
                  size: ord.size,
                  totalPrice: ord.totalPrice,
                  amountLak: ord.amountLak,
                  currency: ord.currency,
                  createdAt: Date.now(),
                });
              });
            }
          }
          isFirstPendingLoadRef.current = false;
          prevPendingIdsRef.current = new Set(list.map((o) => o.id));

          setPendingOrdersList(list);
          setIsPendingLoaded(true);
        },
        (err) => {
          console.warn("Pending Orders warning:", err?.message);
        }
      );
    } catch (e) {
      console.warn("Failed to attach Pending Orders listener:", e);
    }

    try {
      const qPaid = query(
        collection(db, "Orders"),
        where("roundDate", "==", roundDate),
        where("status", "==", "paid"),
        limit(50)
      );
      unsubPaidRef.current = onSnapshot(
        qPaid,
        (snap) => {
          const list = snap.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              shortId: data.shortId || d.id.slice(-6),
              totalPrice: data.totalPrice || 0,
              amountLak: data.amountLak,
              currency: data.currency,
              size: data.size || data.totalQuantity || (data.lotteries ? data.lotteries.length : 0),
              createdAt: data.createdAt,
              phone: data.phone,
              slipUrl: data.slipUrl || data.slip || null,
              customerName: `${data.firstName || ""} ${data.lastName || ""}`.trim() || data.phone || data.userId,
              status: data.status,
            };
          });
          list.sort((a, b) => {
            const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt || 0).getTime();
            const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt || 0).getTime();
            return timeB - timeA;
          });

          // Detect new paid / slip uploaded orders
          if (!isFirstPaidLoadRef.current) {
            const newOrders = list.filter((o) => !prevPaidIdsRef.current.has(o.id));
            if (newOrders.length > 0) {
              if (isAlertSoundEnabled) playOrderChime();
              newOrders.forEach((ord) => {
                addOrderNotification({
                  id: `${ord.id}-${Date.now()}`,
                  orderId: ord.id,
                  shortId: ord.shortId,
                  type: "PAID",
                  title: "แนบสลิปแล้ว (รออนุมัติ)",
                  customerName: ord.customerName,
                  phone: ord.phone,
                  size: ord.size,
                  totalPrice: ord.totalPrice,
                  amountLak: ord.amountLak,
                  currency: ord.currency,
                  slipUrl: ord.slipUrl,
                  createdAt: Date.now(),
                });
              });
            }
          }
          isFirstPaidLoadRef.current = false;
          prevPaidIdsRef.current = new Set(list.map((o) => o.id));

          setPaidOrdersList(list);
          setIsPaidLoaded(true);
        },
        (err) => {
          console.warn("Paid Orders warning:", err?.message);
        }
      );
    } catch (e) {
      console.warn("Failed to attach Paid Orders listener:", e);
    }

    return () => {
      if (unsubPendingRef.current) unsubPendingRef.current();
      if (unsubPaidRef.current) unsubPaidRef.current();
    };
  }, [roundDate, db]);

  // Subscribe to real-time Admin Alerts
  useEffect(() => {
    if (!db) return;
    try {
      const alertsQuery = query(collection(db, "AdminAlerts"), orderBy("createdAt", "desc"), limit(25));
      unsubAlertsRef.current = onSnapshot(
        alertsQuery,
        (snap) => {
          const list = snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));
          const relevant = list.filter(
            (a) => !roundDate || a.roundDate === roundDate || a.status === "unread"
          );

          const hasNewDanger = relevant.some(
            (a) => a.level === "danger" && a.status === "unread" && (!prevAlertCountRef.current || relevant.length > prevAlertCountRef.current)
          );
          if (hasNewDanger && isAlertSoundEnabled) {
            playAlertSound();
          }
          prevAlertCountRef.current = relevant.length;
          setAdminAlerts(relevant);
        },
        (err) => {
          console.warn("AdminAlerts onSnapshot warning:", err?.message);
        }
      );
    } catch (e) {
      console.warn("Failed to attach AdminAlerts listener:", e);
    }

    // 1.2 Complains Real-time Listener (Open / Pending complaints)
    try {
      const qComplains = query(
        collection(db, "Complains"),
        orderBy("createdAt", "desc"),
        limit(25)
      );
      unsubComplainsRef.current = onSnapshot(
        qComplains,
        (snap) => {
          const list = snap.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              shortId: data.shortId || d.id.slice(-5).toUpperCase(),
              topic: data.topic || "เรื่องร้องเรียน",
              firstName: data.firstName || "",
              lastName: data.lastName || "",
              phone: data.phone || "",
              customerName: `${data.firstName || ""} ${data.lastName || ""}`.trim() || data.phone || "-",
              createdAt: data.createdAt,
              status: data.status || "open",
              type: "COMPLAIN",
              isComplain: true,
            };
          });

          // Detect new open complains
          if (!isFirstComplainsLoadRef.current) {
            const newComplains = list.filter(
              (c) => c.status === "open" && !prevComplainIdsRef.current.has(c.id)
            );
            if (newComplains.length > 0) {
              if (isAlertSoundEnabled) playOrderChime();
              newComplains.forEach((comp) => {
                addOrderNotification({
                  id: `${comp.id}-${Date.now()}`,
                  orderId: comp.id,
                  shortId: comp.shortId,
                  type: "COMPLAIN",
                  title: "เรื่องร้องเรียนใหม่",
                  customerName: comp.customerName,
                  phone: comp.phone,
                  size: "-",
                  totalPrice: 0,
                  createdAt: Date.now(),
                }, 5000);
              });
            }
          }
          isFirstComplainsLoadRef.current = false;
          prevComplainIdsRef.current = new Set(list.map((c) => c.id));
          setComplainsList(list);
        },
        (err) => {
          console.warn("Complains listener warning:", err?.message);
        }
      );
    } catch (e) {
      console.warn("Failed to attach Complains listener:", e);
    }

    return () => {
      if (unsubAlertsRef.current) unsubAlertsRef.current();
      if (unsubComplainsRef.current) unsubComplainsRef.current();
    };
  }, [db, roundDate, isAlertSoundEnabled]);

  // Clear single cart item
  const handleClearCart = async (cartId) => {
    if (!cartId) return;
    setClearingCartId(cartId);
    try {
      const res = await callApi("carts-clearCart", { cartId });
      if (res?.status) {
        setActiveCarts((prev) => prev.filter((c) => c.id !== cartId));
      }
    } catch (err) {
      console.error("Failed to clear cart:", err);
    } finally {
      setClearingCartId(null);
    }
  };

  // Resolve Alert action
  const handleResolveAlert = async (alertId) => {
    if (!alertId) return;
    setAdminAlerts((prev) =>
      prev.map((a) => (a.id === alertId ? { ...a, status: "resolved", resolvedAt: Date.now() } : a))
    );
    try {
      if (callApi) {
        await callApi("utils-resolveAdminAlert", { alertId }, { notShowLoading: true });
      } else {
        await executeApi("utils-resolveAdminAlert", { alertId });
      }
    } catch (err) {
      console.error("Failed to resolve alert:", err);
    }
  };

  // Handle resolve complain
  const handleResolveComplain = async (complainId) => {
    if (!complainId) return;
    setComplainsList((prev) =>
      prev.map((c) => (c.id === complainId ? { ...c, status: "closed" } : c))
    );
    try {
      await callApi(
        "complains-updateStatus",
        { id: complainId, value: "closed" },
        { notShowLoading: true }
      );
      toast({
        title: "ปิดเรื่องร้องเรียนเรียบร้อย",
        description: `รหัส #${complainId.slice(-5).toUpperCase()}`,
        className: "bg-[#4DA1A9] text-white",
      });
    } catch (err) {
      console.error("Failed to resolve complain:", err);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    if (!alertId) return;
    setAdminAlerts((prev) => prev.filter((a) => a.id !== alertId));
    try {
      if (callApi) {
        await callApi("utils-deleteAdminAlert", { alertId }, { notShowLoading: true });
      } else {
        await executeApi("utils-deleteAdminAlert", { alertId });
      }
    } catch (err) {
      console.error("Failed to delete alert:", err);
    }
  };



  // Ticket Inventory (Total, On-Block, Off-Block, Sold, Remaining)
  const lottoTotal = Number(rawDashboardData?.currentLotto || stats?.currentLotto || 0);
  const lottoOnBlock = Number(rawDashboardData?.currentLottoOnBlock || stats?.currentLottoOnBlock || 0);

  // Order Status Numbers & Tickets
  const realtimeStats = stats?.realtimeOrderStats || stats || rawDashboardData?.realtimeOrderStats || rawDashboardData;

  const approvedTickets = Number(
    realtimeStats?.approved?.tickets ??
    rawDashboardData?.currentLottoSold ??
    rawDashboardData?.lottoApproved ??
    stats?.lottoApproved ??
    0
  );

  const lottoOffBlock = Number(
    (rawDashboardData?.currentLottoOffBlock !== undefined
      ? rawDashboardData.currentLottoOffBlock
      : stats?.currentLottoOffBlock) ?? Math.max(0, lottoTotal - approvedTickets - lottoOnBlock)
  );
  const pendingTickets = isPendingLoaded
    ? pendingOrdersList.reduce((acc, o) => acc + (Number(o.size) || 0), 0)
    : Number(
      realtimeStats?.pending?.tickets ??
      rawDashboardData?.lottoPending ??
      stats?.lottoPending ??
      0
    );
  const paidTickets = isPaidLoaded
    ? paidOrdersList.reduce((acc, o) => acc + (Number(o.size) || 0), 0)
    : Number(
      realtimeStats?.paid?.tickets ??
      rawDashboardData?.lottoPaid ??
      stats?.lottoPaid ??
      0
    );
  // const lottoSold = Number(rawDashboardData?.currentLottoSold || approvedTickets || 0);
  // const lottoInCart = liveCartCount !== null ? liveCartCount : Number(rawDashboardData?.currentLottoInCart || 0);
  // const lottoRemaining = Math.max(0, lottoTotal - lottoSold);

  const totalOrders = Number(
    realtimeStats?.total?.orders ??
    rawDashboardData?.orderTotal ??
    stats?.orderTotal ??
    0
  );
  const approvedOrders = Number(
    realtimeStats?.approved?.orders ??
    rawDashboardData?.orderApproved ??
    stats?.orderApproved ??
    0
  );
  const canceledOrders = Number(
    realtimeStats?.canceled?.orders ??
    rawDashboardData?.orderCancel ??
    stats?.orderCancel ??
    0
  );
  const pendingOrders = isPendingLoaded
    ? pendingOrdersList.length
    : Number(
      realtimeStats?.pending?.orders ??
      rawDashboardData?.orderPending ??
      stats?.orderPending ??
      0
    );
  const paidOrders = isPaidLoaded
    ? paidOrdersList.length
    : Number(
      realtimeStats?.paid?.orders ??
      rawDashboardData?.orderPaid ??
      stats?.orderPaid ??
      0
    );

  const calculatePercent = (val, total) => {
    if (!total || total === 0) return "0.0%";
    return `${((val / total) * 100).toFixed(1)}%`;
  };

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto pb-12">
      {/* Top Header */}
      <Header
        header={
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="font-black text-gray-900">Admin</span>

          </div>
        }
        subHeader={
          <div className="flex items-center gap-2 flex-wrap text-xs">
            {/* Live realtime badge */}
            <div className="flex items-center gap-1.5 bg-rose-50 border border-rose-200 px-2.5 py-0.5 rounded-md">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
              </span>
              <span className="text-[10px] font-bold text-rose-600 tracking-wider">LIVE REALTIME</span>
            </div>

          </div>
        }
        icon={<RiAdminLine className="text-2xl text-blue-600" />}
      />

      {/* ── Pending User Anonymization Alert (ถ้ามียูสเซอร์ Removed รอเซนเซอร์) ── */}
      {pendingAnonymizeCount > 0 && (
        <div className="mt-3 p-3.5 bg-rose-50 border border-rose-300 rounded-md shadow-2xs flex flex-wrap items-center justify-between gap-3 animate-in fade-in-50">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-rose-600 text-white rounded-md shadow-xs">
              <ShieldAlert className="w-5 h-5" />
            </span>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-bold text-rose-950">
                  มียูสเซอร์สถานะ Removed รอการเซนเซอร์ข้อมูลส่วนบุคคล ({pendingAnonymizeCount} บัญชี)
                </h3>
                {!canAnonymize && (
                  <span className="text-[10px] font-bold bg-rose-200 text-rose-800 px-2 py-0.5 rounded-md">
                    เฉพาะ Super Admin & Dev
                  </span>
                )}
              </div>
              <p className="text-xs text-rose-700 mt-0.5">
                แอดมินได้ทำการเปลี่ยนสถานะเป็น Removed แล้ว {canAnonymize ? "สามารถกดกวาดเซนเซอร์ข้อมูล PII ให้เป็นไปตามกฎหมายได้ทันที" : "ต้องให้ Super Admin หรือ Dev เป็นผู้ดำเนินการกดเซนเซอร์ข้อมูล"}
              </p>
            </div>
          </div>

          {canAnonymize ? (
            <button
              type="button"
              onClick={() => setIsBatchModalOpen(true)}
              className="py-2.5 px-4 bg-rose-600 hover:bg-rose-700 active:opacity-95 text-white text-xs font-bold rounded-md shadow-xs transition flex items-center gap-2 cursor-pointer shrink-0"
            >
              <ShieldAlert className="w-4 h-4" />
              <span>เซนเซอร์ข้อมูลทั้งหมด ({pendingAnonymizeCount})</span>
            </button>
          ) : (
            <button
              type="button"
              disabled
              title="เฉพาะสิทธิ์ Super Admin และ Dev เท่านั้นที่สามารถกดดำเนินการได้"
              className="py-2.5 px-4 bg-slate-200 text-slate-400 border border-slate-300 text-xs font-bold rounded-md transition flex items-center gap-2 cursor-not-allowed shrink-0 select-none"
            >
              <Lock className="w-4 h-4 text-slate-400" />
              <span>เซนเซอร์ข้อมูลทั้งหมด ({pendingAnonymizeCount})</span>
            </button>
          )}
        </div>
      )}

      {/* ── Admin Info & Shift Performance Banner ── */}


      {/* ── Section 1: ภาพรวมสถานะคำสั่งซื้อ & สต็อกประจำงวด (Commented out) ── */}
      {false && (
        <div className="bg-white rounded-md p-3 mt-3 shadow-sm border border-gray-200">
          <div className="flex items-center gap-1.5 mb-2">
            <div className="bg-blue-50 text-blue-600 rounded">
              <FiShoppingBag className="text-xs" />
            </div>
            <h3 className="font-bold text-xs text-gray-800">สถานะคำสั่งซื้อ (รายการ)</h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
            {/* 1. คำสั่งซื้อทั้งหมด */}
            <div className="p-2.5 bg-slate-900 text-white rounded-md shadow-2xs flex flex-col justify-between">
              <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">คำสั่งซื้อทั้งหมด</span>
              <p className="text-xl font-black mt-0.5">
                <AnimatedNumber value={totalOrders} />
              </p>
            </div>

            {/* 2. รอจ่าย (Pending) */}
            <div className="p-2.5 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-md shadow-2xs flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-amber-100 uppercase tracking-wider">รอจ่าย</span>
                {totalOrders > 0 && (
                  <span className="text-[9px] font-bold bg-white/20 px-1 py-0.2 rounded">
                    {calculatePercent(pendingOrders, totalOrders)}
                  </span>
                )}
              </div>
              <p className="text-xl font-black mt-0.5">
                <AnimatedNumber value={pendingOrders} />
              </p>
            </div>

            {/* 3. รออนุมัติ (Paid) */}
            <div className="p-2.5 bg-[#4DA1A9] text-white rounded-md shadow-2xs flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-teal-100 uppercase tracking-wider">รออนุมัติ</span>
                {totalOrders > 0 && (
                  <span className="text-[9px] font-bold bg-white/20 px-1 py-0.2 rounded">
                    {calculatePercent(paidOrders, totalOrders)}
                  </span>
                )}
              </div>
              <p className="text-xl font-black mt-0.5">
                <AnimatedNumber value={paidOrders} />
              </p>
            </div>

            {/* 4. อนุมัติแล้ว */}
            <div className="p-2.5 bg-gradient-to-r from-emerald-600 to-teal-700 text-white rounded-md shadow-2xs flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-emerald-100 uppercase tracking-wider">อนุมัติแล้ว</span>
                {totalOrders > 0 && (
                  <span className="text-[9px] font-bold bg-white/20 px-1 py-0.2 rounded">
                    {calculatePercent(approvedOrders, totalOrders)}
                  </span>
                )}
              </div>
              <p className="text-xl font-black mt-0.5">
                <AnimatedNumber value={approvedOrders} />
              </p>
            </div>

            {/* 5. ยกเลิก */}
            <div className="p-2.5 bg-gradient-to-r from-rose-600 to-pink-700 text-white rounded-md shadow-2xs flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-rose-100 uppercase tracking-wider">ยกเลิก</span>
                {totalOrders > 0 && (
                  <span className="text-[9px] font-bold bg-white/20 px-1 py-0.2 rounded">
                    {calculatePercent(canceledOrders, totalOrders)}
                  </span>
                )}
              </div>
              <p className="text-xl font-black mt-0.5">
                <AnimatedNumber value={canceledOrders} />
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Scheduled Tasks Alert Banner (Universal Queue) ── */}
      <ScheduledTasksWidget variant="adminCenter" className="mt-3" />

      {/* ── Section 2: ระบบแจ้งเตือน (Security & Complain Alerts) & Top 10 คงเหลือสูงสุด ── */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3 mt-3">
        {/* Left (8/12): Live Security & Operational Alerts Panel */}
        <div className="xl:col-span-8 flex flex-col">
          {(() => {
            // Combine AdminAlerts and Complains (filter out COMPLAIN in alerts to prevent duplicate rows)
            const allItems = [
              ...adminAlerts.filter((a) => a.type !== "COMPLAIN").map((a) => ({ ...a, itemSource: "alert" })),
              ...complainsList.map((c) => ({
                ...c,
                itemSource: "complain",
                message: c.topic,
                level: "info",
              })),
            ].sort((a, b) => {
              const timeA = a.createdAt ? (a.createdAt.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt).getTime()) : 0;
              const timeB = b.createdAt ? (b.createdAt.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt).getTime()) : 0;
              return timeB - timeA;
            });

            const filteredAlerts = allItems.filter((a) => {
              if (alertFilter === "danger") return a.level === "danger" || a.type === "DUPLICATE_SLIP";
              if (alertFilter === "warning") return a.level === "warning" || a.type === "NO_SLIP_ID";
              if (alertFilter === "complain") return a.type === "COMPLAIN";
              return true;
            });

            const unreadAlertsCount = adminAlerts.filter((a) => a.status === "unread").length;
            const openComplainsCount = complainsList.filter((c) => c.status === "open").length;
            const totalNewIssues = unreadAlertsCount + openComplainsCount;
            const duplicateCount = adminAlerts.filter((a) => a.type === "DUPLICATE_SLIP").length;
            const noSlipIdCount = adminAlerts.filter((a) => a.type === "NO_SLIP_ID").length;

            return (
              <div className="bg-white rounded-md p-3 shadow-sm border border-gray-200 h-full flex flex-col justify-between">
                {/* Alert Header */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2 mb-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-md bg-[#c15b78]/10 text-[#c15b78] flex items-center justify-center border border-[#c15b78]/20 shrink-0">
                      <RiAlarmWarningLine className="text-base" />
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <h3 className="font-bold text-sm text-gray-900">
                          ระบบแจ้งเตือน (Security & Complain Alerts)
                        </h3>
                      </div>
                      <p className="text-[11px] text-gray-500 font-medium">
                        ตรวจจับสลิปซ้ำ & ข้อร้องเรียน Real-time:{" "}
                        <strong className="text-[#c15b78] font-bold">{totalNewIssues} รายการใหม่</strong>
                      </p>
                    </div>
                  </div>

                  {/* Filter tabs and sound toggle */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-md text-xs">
                      <button
                        onClick={() => setAlertFilter("all")}
                        className={`px-2 py-0.5 rounded-md font-bold transition ${alertFilter === "all" ? "bg-white text-gray-900 shadow-2xs" : "text-gray-500 hover:text-gray-800"
                          }`}
                      >
                        ทั้งหมด ({allItems.length})
                      </button>
                      <button
                        onClick={() => setAlertFilter("danger")}
                        className={`px-2 py-0.5 rounded-md font-bold transition ${alertFilter === "danger" ? "bg-[#c15b78] text-white shadow-2xs" : "text-gray-500 hover:text-[#c15b78]"
                          }`}
                      >
                        🔴 สลิปซ้ำ ({duplicateCount})
                      </button>
                      <button
                        onClick={() => setAlertFilter("warning")}
                        className={`px-2 py-0.5 rounded-md font-bold transition ${alertFilter === "warning" ? "bg-[#FAB12F] text-white shadow-2xs" : "text-gray-500 hover:text-[#FAB12F]"
                          }`}
                      >
                        🟡 ไม่พบ QR ({noSlipIdCount})
                      </button>
                      <button
                        onClick={() => setAlertFilter("complain")}
                        className={`px-2 py-0.5 rounded-md font-bold transition ${alertFilter === "complain" ? "bg-purple-600 text-white shadow-2xs" : "text-gray-500 hover:text-purple-600"
                          }`}
                      >
                        🟣 ร้องเรียน ({openComplainsCount})
                      </button>
                    </div>

                    <button
                      onClick={() => setIsAlertSoundEnabled(!isAlertSoundEnabled)}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold border transition shadow-2xs ${isAlertSoundEnabled ? "bg-[#c15b78]/10 text-[#c15b78] border-[#c15b78]/30" : "bg-gray-50 text-gray-500 border-gray-200"
                        }`}
                      title={isAlertSoundEnabled ? "ปิดเสียงแจ้งเตือน" : "เปิดเสียงแจ้งเตือนเมื่อพบสลิปซ้ำ"}
                    >
                      {isAlertSoundEnabled ? <FiVolume2 className="text-[#c15b78]" /> : <FiVolumeX />}
                      <span className="hidden sm:inline">{isAlertSoundEnabled ? "เปิดเสียง" : "ปิดเสียง"}</span>
                    </button>
                  </div>
                </div>

                {/* Alert Feed Content - Horizontal Table Layout */}
                {filteredAlerts.length === 0 ? (
                  <div className="py-8 px-3 flex items-center justify-center gap-2 text-gray-400 bg-slate-50/50 border border-dashed border-gray-200 rounded-md text-xs">
                    <FiCheckCircle className="text-[rgb(59,176,101)] text-sm" />
                    <span>ไม่พบรายการแจ้งเตือนที่ผิดปกติ ระบบตรวจสลิปและคำสั่งซื้อทำงานราบรื่น</span>
                  </div>
                ) : (
                  <div className="overflow-x-auto max-h-[340px] overflow-y-auto rounded-md border border-gray-200 shadow-2xs">
                    <Table>
                      <TableHeader className="sticky top-0 z-10 bg-slate-50 shadow-xs">
                        <TableRow className="border-b border-gray-200">
                          <TableHead className="w-12 text-center text-xs font-bold text-gray-700 py-1.5">ลำดับ</TableHead>
                          <TableHead className="text-center text-xs font-bold text-gray-700 py-1.5">วัน/เวลา</TableHead>
                          <TableHead className="text-center text-xs font-bold text-gray-700 py-1.5">ประเภท</TableHead>
                          <TableHead className="text-xs font-bold text-gray-700 py-1.5">ไอดี</TableHead>
                          <TableHead className="text-xs font-bold text-gray-700 py-1.5">รายละเอียดแจ้งเตือน</TableHead>
                          <TableHead className="text-xs font-bold text-gray-700 py-1.5">ลูกค้า / เบอร์โทร</TableHead>
                          <TableHead className="text-right text-xs font-bold text-gray-700 py-1.5">ยอดเงิน</TableHead>
                          <TableHead className="text-right text-xs font-bold text-gray-700 py-1.5 pr-3">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAlerts.map((alert, idx) => {
                          const isComplain = alert.type === "COMPLAIN";
                          const isDanger = alert.level === "danger" || alert.type === "DUPLICATE_SLIP";
                          const isWarning = alert.level === "warning" || alert.type === "NO_SLIP_ID";
                          const isResolved = isComplain ? alert.status === "closed" : alert.status === "resolved";

                          return (
                            <TableRow
                              key={alert.id}
                              className={`border-b border-gray-100 last:border-0 transition ${isResolved
                                ? "bg-gray-50/70 opacity-60"
                                : isDanger
                                  ? "bg-rose-50/40 hover:bg-rose-50/70"
                                  : isWarning
                                    ? "bg-amber-50/30 hover:bg-amber-50/60"
                                    : isComplain
                                      ? "bg-purple-50/40 hover:bg-purple-50/70"
                                      : "hover:bg-slate-50"
                                }`}
                            >
                              <TableCell className="text-center font-mono text-xs text-gray-500 py-1.5">
                                {idx + 1}
                              </TableCell>
                              <TableCell className="text-center text-[11px] text-gray-600 font-medium py-1.5 whitespace-nowrap">
                                {(() => {
                                  if (!alert.createdAt) return "-";
                                  const dateObj = alert.createdAt.toMillis
                                    ? new Date(alert.createdAt.toMillis())
                                    : new Date(alert.createdAt);
                                  if (isNaN(dateObj.getTime())) return "-";
                                  const day = String(dateObj.getDate()).padStart(2, "0");
                                  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
                                  const year = String(dateObj.getFullYear()).slice(-2);
                                  const time = dateObj.toLocaleTimeString("th-TH", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  });
                                  return (
                                    <div className="leading-tight">
                                      <span className="font-mono text-gray-700 font-semibold">
                                        {day}/{month}/{year}
                                      </span>
                                      <span className="block text-[10px] text-gray-400 font-normal">
                                        {time} น.
                                      </span>
                                    </div>
                                  );
                                })()}
                              </TableCell>
                              <TableCell className="text-center py-1.5 whitespace-nowrap">
                                <span
                                  className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-md ${isDanger
                                    ? "bg-[#c15b78] text-white"
                                    : isWarning
                                      ? "bg-[#FAB12F] text-white"
                                      : isComplain
                                        ? "bg-purple-600 text-white"
                                        : "bg-[#4DA1A9] text-white"
                                    }`}
                                >
                                  {isDanger ? "สลิปซ้ำ" : isWarning ? "ไม่พบ QR" : isComplain ? "ร้องเรียน" : "แจ้งเตือน"}
                                </span>
                              </TableCell>
                              <TableCell className="py-1.5 whitespace-nowrap">
                                {isComplain ? (
                                  <button
                                    onClick={() => window.open(`/complains`, "_blank")}
                                    className="flex items-center gap-1 text-xs font-bold text-purple-600 hover:text-purple-800 font-mono"
                                    title="เปิดดูหน้าร้องเรียน"
                                  >
                                    <span>#{alert.shortId}</span>
                                    <FiExternalLink className="text-[10px]" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => window.open(`/orders/${alert.orderId}`, "_blank")}
                                    className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-800 font-mono"
                                    title="เปิดดูคำสั่งซื้อในแท็บใหม่"
                                  >
                                    <span>#{alert.shortId || alert.orderId?.slice(-6)}</span>
                                    <FiExternalLink className="text-[10px]" />
                                  </button>
                                )}
                              </TableCell>
                              <TableCell className="py-1.5">
                                <p className="text-xs font-semibold text-gray-800 line-clamp-1" title={alert.message || alert.title}>
                                  {alert.message || alert.title}
                                </p>
                              </TableCell>
                              <TableCell className="py-1.5 whitespace-nowrap">
                                <div className="text-xs">
                                  <span className="font-semibold text-gray-900 block truncate max-w-[140px]">
                                    {alert.customerName && alert.customerName !== "-" ? alert.customerName : "ลูกค้าทั่วไป"}
                                  </span>
                                  {alert.phone && alert.phone !== "-" && (
                                    <span className="text-[10px] text-gray-500 font-mono">{alert.phone}</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right py-1.5 whitespace-nowrap">
                                {alert.totalPrice > 0 ? (
                                  <span className="font-black text-xs text-emerald-700">
                                    ฿{Number(alert.totalPrice).toLocaleString()}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-xs">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right py-1.5 pr-2 whitespace-nowrap">
                                <div className="flex items-center justify-end gap-1">
                                  {alert.slipUrl && (
                                    <button
                                      onClick={() => setSelectedSlipModal(alert)}
                                      className="flex items-center gap-1 bg-white hover:bg-slate-100 text-gray-700 text-[10px] font-bold px-2 py-1 rounded-md border border-gray-200 transition shadow-2xs"
                                      title="ดูรูปสลิป"
                                    >
                                      <FiEye className="text-blue-600 text-xs" />
                                      <span>สลิป</span>
                                    </button>
                                  )}
                                  {isComplain ? (
                                    <button
                                      onClick={() => window.open(`/complains`, "_blank")}
                                      className="flex items-center gap-1 bg-white hover:bg-purple-50 text-purple-600 text-[10px] font-bold px-2 py-1 rounded-md border border-purple-200 transition shadow-2xs"
                                      title="เปิดดูหน้าร้องเรียน"
                                    >
                                      <FiExternalLink className="text-xs" />
                                      <span>ดูเรื่อง</span>
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => window.open(`/orders/${alert.orderId}`, "_blank")}
                                      className="flex items-center gap-1 bg-white hover:bg-slate-100 text-blue-600 text-[10px] font-bold px-2 py-1 rounded-md border border-blue-200 transition shadow-2xs"
                                      title="เปิดดูคำสั่งซื้อในแท็บใหม่"
                                    >
                                      <FiExternalLink className="text-xs" />
                                      <span>ออเดอร์</span>
                                    </button>
                                  )}

                                  {!isResolved ? (
                                    <button
                                      onClick={() => isComplain ? handleResolveComplain(alert.id) : handleResolveAlert(alert.id)}
                                      className="flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-1 rounded-md border border-emerald-200 transition shadow-2xs"
                                      title="ทำเครื่องหมายว่าตรวจสอบเรียบร้อยแล้ว"
                                    >
                                      <FiCheck className="text-xs" />
                                      <span>เคลียร์</span>
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => !isComplain && handleDeleteAlert(alert.id)}
                                      className={`p-1 text-gray-400 rounded-md transition ${!isComplain ? "hover:text-rose-600" : "opacity-0 pointer-events-none"}`}
                                      title="ลบรายการแจ้งเตือนนี้"
                                    >
                                      <RiDeleteBin7Line className="text-xs" />
                                    </button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Right (4/12): Top 10 คงเหลือสูงสุด */}
        <div className="xl:col-span-4 flex flex-col">
          <LastTwoDigitsStockPanel
            currentLottoLastTwo={rawDashboardData?.currentLottoLastTwo}
            currentBlocks={rawDashboardData?.currentBlocks}
            roundDate={roundDate || configs?.roundDate}
            limitLow={10}
            limitHigh={10}
            compact={true}
            className="mt-0 h-full flex flex-col"
          />
        </div>
      </div>

      {/* ── Section 3: คำขอย้ายเจ้าของ (Transfer Requests) & ลูกค้าที่ปักหมุดไว้ (Favorite Customers) (2-Column Grid) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
        {/* Col 1: คำขอย้ายเจ้าของคำสั่งซื้อสลาก (Transfer Requests) */}
        <div className="bg-white rounded-md p-3.5 shadow-sm border border-gray-200 flex flex-col justify-between">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2.5 mb-2.5">
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-md flex items-center justify-center border shrink-0 ${pendingTransferRequests.length > 0
                  ? "bg-amber-50 text-amber-600 border-amber-200"
                  : "bg-slate-50 text-slate-500 border-slate-200"
                  }`}>
                  <ArrowRightLeft className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2">
                    <span>คำขอย้ายคำสั่งซื้อ (Transfer Requests)</span>
                    {pendingTransferRequests.length > 0 && (
                      <span className="px-2 py-0.5 rounded-md text-[11px] font-extrabold bg-rose-500 text-white animate-pulse">
                        รออนุมัติ {pendingTransferRequests.length} รายการ
                      </span>
                    )}
                  </h3>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => window.open("/orders/transfer-requests", "_blank")}
                  className="px-2.5 py-1 text-xs font-bold text-blue hover:text-blue-700 bg-blue/5 hover:bg-blue/10 border border-blue/20 rounded-md transition flex items-center gap-1 cursor-pointer"
                >
                  <span>ดูทั้งหมด</span>
                  <FiExternalLink className="text-xs" />
                </button>
              </div>
            </div>

            {pendingTransferRequests.length === 0 ? (
              <div className="h-44 sm:h-[220px] py-4 text-center text-gray-400 flex flex-col items-center justify-center gap-2 text-xs font-medium border border-dashed border-gray-200 rounded-md">
                <FiCheckCircle className="text-emerald-500 text-base" />
                <span>ไม่มีคำขอย้ายคำสั่งซื้อที่ค้างรอดำเนินการ</span>
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[220px] overflow-y-auto rounded-md border border-amber-200/80 shadow-2xs">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-amber-50/90 backdrop-blur-xs border-b border-amber-200/80 shadow-xs">
                    <TableRow>
                      <TableHead className="text-xs font-bold text-gray-700 py-1.5 px-2">คำสั่งซื้อ</TableHead>
                      <TableHead className="text-xs font-bold text-gray-700 py-1.5 px-2">จากเดิม ➡️ ไปยัง</TableHead>
                      <TableHead className="text-xs font-bold text-gray-700 py-1.5 px-2">สาเหตุ</TableHead>
                      <TableHead className="text-right text-xs font-bold text-gray-700 py-1.5 pr-2.5">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingTransferRequests.map((req) => (
                      <TableRow key={req.id} className="hover:bg-amber-50/40 transition border-b border-gray-100 last:border-0">
                        <TableCell className="py-1.5 px-2">
                          <div className="flex flex-col">
                            <span
                              onClick={() => window.open(`/orders/${req.orderId}`, "_blank")}
                              className="font-mono font-bold text-xs text-blue hover:underline cursor-pointer flex items-center gap-1"
                            >
                              {req.shortId ? `#${req.shortId}` : req.orderId?.slice(-6)}
                              <FiExternalLink className="text-[9px] opacity-60" />
                            </span>
                            <span className="text-[10px] text-gray-500">
                              {req.totalPrice ? `${Number(req.totalPrice).toLocaleString()} ฿` : ""}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="py-1.5 px-2">
                          <div className="flex items-center gap-1 text-xs">
                            <span className="font-semibold text-gray-700 truncate max-w-[90px]">
                              {req.fromUser?.phone || req.fromUser?.firstName || "เดิม"}
                            </span>
                            <ArrowRightLeft className="w-3 h-3 text-gray-400 shrink-0" />
                            <span className="font-bold text-blue truncate max-w-[90px]">
                              {req.toUser?.phone || req.toUser?.firstName || "ใหม่"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="py-1.5 px-2 max-w-[150px]">
                          <p className="text-xs text-gray-800 truncate" title={req.reason}>
                            {req.reason}
                          </p>
                          {req.detail && (
                            <p className="text-[10px] text-gray-400 truncate">{req.detail}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-right py-1.5 pr-2.5">
                          <button
                            type="button"
                            onClick={() => setSelectedTransferModal(req)}
                            className="px-2 py-1 text-[11px] font-bold rounded-md bg-navy hover:bg-navy/90 text-white shadow-2xs transition cursor-pointer flex items-center gap-1 ml-auto"
                          >
                            <FiEye className="text-xs" />
                            <span>ตรวจสอบ</span>
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        {/* Col 2: ลูกค้าที่ปักหมุดไว้ (Favorite Customers) */}
        <div className="bg-white rounded-md p-3.5 shadow-sm border border-gray-200 flex flex-col justify-between">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2.5 mb-2.5">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-amber-50 text-amber-500 flex items-center justify-center border border-amber-200 shrink-0">
                  <RiStarFill className="text-base" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-sm text-gray-900">
                      ลูกค้าที่ปักหมุดไว้ (Favorite Customers)
                    </h3>
                    <span className="text-[11px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-md">
                      {favoriteUsers.length} ท่าน
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => window.open("/users", "_blank")}
                className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-md border border-blue-200 transition shadow-2xs cursor-pointer"
              >
                <FiUsers className="text-xs" />
                <span>ค้นหาลูกค้าทั้งหมด</span>
              </button>
            </div>

            {favoriteUsers.length === 0 ? (
              <div className="h-44 sm:h-[220px] py-4 px-4 flex flex-col items-center justify-center gap-1.5 text-center bg-slate-50/60 border border-dashed border-gray-200 rounded-md">
                <div className="w-9 h-9 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-400">
                  <RiStarFill className="text-lg opacity-60" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-700">ยังไม่มีลูกค้าที่ปักหมุดไว้ในรายการโปรด</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    สามารถกดปุ่ม ⭐ ที่หน้ารายชื่อลูกค้า (<a href="/users" target="_blank" rel="noreferrer" className="text-blue-600 font-bold underline">/users</a>) เพื่อปักหมุดได้
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 max-h-[220px] overflow-y-auto pr-1">
                {favoriteUsers.map((fav) => {
                  const displayName = [fav.firstName, fav.lastName].filter(Boolean).join(" ") || fav.phone || `ID: ${fav.id}`;
                  return (
                    <div
                      key={fav.id}
                      onClick={() => window.open(`/user/${fav.id}`, "_blank")}
                      className="group relative flex flex-col justify-between p-2.5 bg-slate-50/70 hover:bg-white border border-gray-200 hover:border-amber-400 rounded-md transition-all shadow-2xs hover:shadow-sm cursor-pointer"
                    >
                      <div>
                        <div className="flex items-start justify-between gap-1.5 mb-1.5">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <div className="w-7 h-7 rounded-md bg-amber-500/10 text-amber-700 border border-amber-200 flex items-center justify-center font-bold text-xs shrink-0 overflow-hidden">
                              {fav.profileImage ? (
                                <img src={fav.profileImage} alt={displayName} className="w-full h-full object-cover" />
                              ) : (
                                <span>{(fav.firstName?.[0] || fav.phone?.[0] || "U").toUpperCase()}</span>
                              )}
                            </div>
                            <div className="min-w-0">
                              <h5 className="font-bold text-xs text-gray-900 truncate group-hover:text-blue-600 transition-colors" title={displayName}>
                                {displayName}
                              </h5>
                              <p className="text-[10px] text-gray-500 font-mono truncate">
                                {fav.phone || `ID: ${fav.id}`}
                              </p>
                            </div>
                          </div>

                          <button
                            type="button"
                            title="นำออกจากรายการโปรด"
                            onClick={(e) => handleRemoveFavorite(e, fav.id, displayName)}
                            className="p-1 text-gray-300 hover:text-rose-500 hover:bg-rose-50 rounded-md transition shrink-0 cursor-pointer"
                          >
                            <FiX className="text-xs" />
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 pt-1.5 border-t border-gray-100 mt-1">
                        <button
                          type="button"
                          title="เข้าสู่ระบบแทนลูกค้าเพื่อสั่งซื้อสลาก"
                          disabled={proxyLoginId === fav.id}
                          onClick={(e) => handleProxyLogin(e, fav.id)}
                          className="flex-1 flex items-center justify-center gap-1 bg-blue-50 hover:bg-blue-600 text-blue-600 hover:text-white border border-blue-200 hover:border-blue-600 text-[10px] font-bold py-1 px-1.5 rounded-md transition-all shadow-2xs disabled:opacity-50 cursor-pointer"
                        >
                          {proxyLoginId === fav.id ? (
                            <FiRefreshCw className="text-xs animate-spin" />
                          ) : (
                            <FiLogIn className="text-xs" />
                          )}
                          <span>สั่งซื้อแทน</span>
                        </button>

                        <button
                          type="button"
                          title="เปิดดูข้อมูลลูกค้า"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`/user/${fav.id}`, "_blank");
                          }}
                          className="p-1 text-gray-500 hover:text-gray-900 bg-white hover:bg-gray-100 border border-gray-200 rounded-md text-[10px] font-bold transition shadow-2xs cursor-pointer"
                        >
                          <FiExternalLink className="text-xs" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Section 4: Realtime Live Carts & Recent Orders (2-Column Grid) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
        {/* Column 1 (1/3): Realtime Live Shopping Carts */}
        <div className="bg-white rounded-md p-2 shadow-sm border border-gray-200 flex flex-col justify-between">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2 mb-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-orange-50 text-orange-600 flex items-center justify-center border border-orange-100 shrink-0">
                  <FiShoppingCart className="text-sm" />
                </div>
                <h3 className="font-bold text-sm text-gray-900">
                  สลากในตะกร้าลูกค้า
                </h3>
              </div>
              <div className="flex gap-2 items-center">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                </span>
                <p className="text-[11px] text-gray-500 font-medium">
                  เลือกซื้อ: <strong className="text-orange-600 font-bold">{activeCarts.length} ตะกร้า</strong> (
                  <strong className="text-orange-600 font-bold">{liveCartCount || 0} ใบ</strong>)
                </p>
              </div>
            </div>

            {/* Cart Table List */}
            {activeCarts.length === 0 ? (
              <div className="h-56 flex flex-col items-center justify-center text-gray-400 border border-dashed border-gray-200 rounded-md">
                <FiShoppingCart className="text-2xl mb-1 opacity-30 text-orange-400" />
                <p className="text-xs font-semibold text-gray-500">ไม่มีสลากในตะกร้าขณะนี้</p>
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[380px] overflow-y-auto rounded-md border border-gray-200 shadow-2xs">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-slate-50 shadow-xs">
                    <TableRow className="border-b border-gray-200">
                      <TableHead className="w-10 text-center text-xs font-bold text-gray-700 py-1.5">#</TableHead>
                      <TableHead className="text-xs font-bold text-gray-700 py-1.5">ผู้ใช้</TableHead>
                      <TableHead className="text-center text-xs font-bold text-gray-700 py-1.5">จำนวน</TableHead>
                      <TableHead className="text-center text-xs font-bold text-gray-700 py-1.5">หมดอายุ</TableHead>
                      <TableHead className="text-right text-xs font-bold text-gray-700 py-1.5 pr-2">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeCarts.map((cart, idx) => {
                      const isExpiringSoon = cart.expireAt && cart.expireAt - Date.now() < 3 * 60 * 1000;
                      const isExpired = cart.expireAt && cart.expireAt < Date.now();
                      const isGuest = cart.id?.startsWith("guest-");
                      const userPhone = cart.otp?.phone || cart.phone;
                      const isClearing = clearingCartId === cart.id;

                      return (
                        <TableRow key={cart.id || idx} className="hover:bg-slate-50/80 transition border-b border-gray-100 last:border-0">
                          <TableCell className="text-center font-mono text-xs text-gray-500 py-1.5">
                            {idx + 1}
                          </TableCell>
                          <TableCell className="py-1.5">
                            <div className="flex items-center gap-1">
                              {userPhone ? (
                                <span className="font-bold text-xs text-gray-900 font-mono">
                                  {userPhone}
                                </span>
                              ) : !isGuest ? (
                                <a
                                  href={`/user/${cart.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="group inline-flex items-center gap-1 font-bold text-xs bg-slate-100 hover:bg-slate-200 text-slate-800 px-1.5 py-0.5 rounded-md border border-slate-300/80 transition font-mono"
                                  title={`เปิดข้อมูลลูกค้า: ${cart.id}`}
                                >
                                  <span>{cart.id.length > 10 ? `${cart.id.slice(0, 4)}...${cart.id.slice(-3)}` : cart.id}</span>
                                  <FiExternalLink className="text-[10px] text-slate-400 group-hover:text-slate-700" />
                                </a>
                              ) : (
                                <span className="font-mono text-[11px] text-gray-500 bg-gray-100 px-1 py-0.5 rounded border border-gray-200">
                                  Guest ({cart.id.slice(-4)})
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center py-1.5">
                            <span className="font-extrabold text-xs text-orange-600 bg-orange-50 px-1.5 py-0.2 rounded-md border border-orange-100">
                              {cart.size || 0} ใบ
                            </span>
                          </TableCell>
                          <TableCell className="text-center py-1.5">
                            {cart.expireAt ? (
                              <span
                                className={`text-[10px] font-bold px-1.5 py-0.2 rounded-md ${isExpired
                                  ? "bg-rose-100 text-rose-700 animate-pulse"
                                  : isExpiringSoon
                                    ? "bg-amber-100 text-amber-800"
                                    : "bg-slate-100 text-slate-700"
                                  }`}
                              >
                                {new Date(cart.expireAt).toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })} น.
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right py-1.5 pr-2">
                            <button
                              onClick={() => handleClearCart(cart.id)}
                              disabled={isClearing}
                              className="p-1 rounded-md bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 transition shadow-2xs disabled:opacity-50"
                              title="ยกเลิกและเคลียร์ตะกร้าของลูกค้ารายนี้"
                            >
                              <RiDeleteBin7Line className="text-xs" />
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        {/* Column 2 (1/3): Real-time Orders (แบ่งเป็น 2 คอลัมน์ ซ้าย: รอจ่าย, ขวา: รออนุมัติ) */}
        <div className="bg-white rounded-md p-2.5 sm:p-3 shadow-sm border border-gray-200 flex flex-col justify-between">
          <div>
            {/* Main Header */}
            <div className="flex items-center border-b border-gray-100 pb-2 mb-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-blue-50 text-blue-600 border border-blue-200 flex items-center justify-center shrink-0">
                  <FiShoppingBag className="text-sm" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                    <span>คำสั่งซื้อ Real-time</span>
                  </h3>

                </div>
                {/* <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.2 rounded border border-amber-200/80">
                    รอจ่าย: {pendingOrdersList.length}
                  </span>
                  <span className="text-[10px] font-semibold text-sky-700 bg-sky-50 px-1.5 py-0.2 rounded border border-sky-200/80">
                    รออนุมัติ: {paidOrdersList.length}
                  </span>
                </div> */}
              </div>


            </div>

            {/* 2-Column Grid Inside Panel: Col 1 = รอจ่าย, Col 2 = รออนุมัติ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {/* Col 1: คำสั่งซื้อรอจ่าย */}
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1 text-[11px] font-bold text-amber-800">
                    <FiClock className="text-amber-600 text-xs" />
                    <span>คำสั่งซื้อรอจ่าย</span>
                  </div>
                  <span className="text-[10px] font-mono font-bold text-amber-700 bg-amber-100/80 px-1.5 py-0.2 rounded-full">
                    {pendingOrdersList.length}
                  </span>
                </div>

                {pendingOrdersList.length === 0 ? (
                  <div className="h-44 flex flex-col items-center justify-center text-gray-400 border border-dashed border-gray-200 rounded-md">
                    <FiAlertCircle className="text-xl mb-1 opacity-30 text-amber-400" />
                    <p className="text-[11px] font-semibold text-gray-400">ไม่มีคำสั่งซื้อรอจ่าย</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto h-44 overflow-y-auto rounded-md border border-amber-200/80 shadow-2xs">
                    <Table>
                      <TableHeader className="sticky top-0 z-10 bg-amber-50/90 backdrop-blur-xs shadow-xs">
                        <TableRow className="border-b border-amber-200">
                          <TableHead className="text-xs font-bold text-gray-700 py-1 px-1.5">ID</TableHead>
                          <TableHead className="text-center text-xs font-bold text-gray-700 py-1 px-1">จำนวน</TableHead>
                          <TableHead className="text-right text-xs font-bold text-gray-700 py-1 px-1.5">ราคา</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingOrdersList.map((order) => (
                          <TableRow
                            key={order.id}
                            onClick={() => window.open(`/orders/${order.id}`, "_blank")}
                            className="hover:bg-amber-50/60 cursor-pointer transition border-b border-gray-100 last:border-0"
                            title={`กดเพื่อเปิดคำสั่งซื้อ #${order.shortId || order.id?.slice(-6)} ในแท็บใหม่`}
                          >
                            <TableCell className="py-1 px-1.5">
                              <div className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-800">
                                <span className="font-mono">{order.shortId || order.id?.slice(-6)}</span>
                                <FiExternalLink className="text-[9px] opacity-60 shrink-0" />
                              </div>
                              {order.expireAt && (
                                (() => {
                                  const expMillis = order.expireAt?.toMillis ? order.expireAt.toMillis() : new Date(order.expireAt).getTime();
                                  const isExpired = Date.now() > expMillis;
                                  return (
                                    <span className={`text-[9px] block font-semibold ${isExpired ? "text-rose-600 font-bold animate-pulse" : "text-amber-700"}`}>
                                      หมดอายุ {new Date(expMillis).toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })} น.
                                    </span>
                                  );
                                })()
                              )}
                            </TableCell>
                            <TableCell className="text-center py-1 px-1">
                              <span className="font-bold text-xs bg-gray-100 text-gray-800 px-1 py-0.2 rounded-md border border-gray-200 whitespace-nowrap">
                                {order.size} ใบ
                              </span>
                            </TableCell>
                            <TableCell className="text-right py-1 px-1.5">
                              <span className="font-extrabold text-xs text-amber-600 whitespace-nowrap">
                                {order.currency === "lak" && order.amountLak
                                  ? `${Number(order.amountLak).toLocaleString()} กีบ`
                                  : `${Number(order.totalPrice).toLocaleString()} ฿`}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Col 2: คำสั่งซื้อรออนุมัติ */}
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1 text-[11px] font-bold text-sky-800">
                    <FiCheckCircle className="text-sky-600 text-xs" />
                    <span>คำสั่งซื้อรออนุมัติ</span>
                  </div>
                  <span className="text-[10px] font-mono font-bold text-sky-700 bg-sky-100/80 px-1.5 py-0.2 rounded-full">
                    {paidOrdersList.length}
                  </span>
                </div>

                {paidOrdersList.length === 0 ? (
                  <div className="h-44 flex flex-col items-center justify-center text-gray-400 border border-dashed border-gray-200 rounded-md">
                    <FiCheckCircle className="text-xl mb-1 opacity-30 text-sky-400" />
                    <p className="text-[11px] font-semibold text-gray-400">ไม่มีคำสั่งซื้อรออนุมัติ</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto h-44 overflow-y-auto rounded-md border border-sky-200/80 shadow-2xs">
                    <Table>
                      <TableHeader className="sticky top-0 z-10 bg-sky-50/90 backdrop-blur-xs shadow-xs">
                        <TableRow className="border-b border-sky-200">
                          <TableHead className="text-xs font-bold text-gray-700 py-1 px-1.5">ID</TableHead>
                          <TableHead className="text-center text-xs font-bold text-gray-700 py-1 px-1">จำนวน</TableHead>
                          <TableHead className="text-right text-xs font-bold text-gray-700 py-1 px-1.5">ราคา / สลิป</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paidOrdersList.map((order) => (
                          <TableRow
                            key={order.id}
                            className="hover:bg-sky-50/60 transition border-b border-gray-100 last:border-0"
                          >
                            <TableCell className="py-1 px-1.5">
                              <div
                                onClick={() => window.open(`/orders/${order.id}`, "_blank")}
                                className="flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-800 cursor-pointer"
                                title="เปิดดูคำสั่งซื้อในแท็บใหม่"
                              >
                                <span className="font-mono">{order.shortId || order.id?.slice(-6)}</span>
                                <FiExternalLink className="text-[9px] opacity-60 shrink-0" />
                              </div>
                              {order.createdAt && (
                                <span className="text-[9px] text-gray-400 block font-normal">
                                  {new Date(order.createdAt?.toMillis ? order.createdAt.toMillis() : order.createdAt).toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })} น.
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center py-1 px-1">
                              <span className="font-bold text-xs bg-gray-100 text-gray-800 px-1 py-0.2 rounded-md border border-gray-200 whitespace-nowrap">
                                {order.size} ใบ
                              </span>
                            </TableCell>
                            <TableCell className="text-right py-1 px-1.5">
                              <div className="flex items-center justify-end gap-1.5">
                                <span className="font-extrabold text-xs text-sky-600 whitespace-nowrap">
                                  {order.currency === "lak" && order.amountLak
                                    ? `${Number(order.amountLak).toLocaleString()} กีบ`
                                    : `${Number(order.totalPrice).toLocaleString()} ฿`}
                                </span>
                                {order.slipUrl && (
                                  <button
                                    onClick={() => setQuickOrderSlip(order)}
                                    className="p-1 rounded bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 text-[10px] font-bold transition shadow-2xs cursor-pointer"
                                    title="ดูสลิปคำสั่งซื้อนี้"
                                  >
                                    <FiEye className="text-xs" />
                                  </button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Slip Image Preview Modal (Security Alerts) ── */}
      {selectedSlipModal && (
        <div
          className="fixed inset-0 z-50 bg-black/75 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setSelectedSlipModal(null)}
        >
          <div
            className="bg-white rounded-md p-4 max-w-md w-full shadow-2xl border border-gray-200 max-h-[90vh] flex flex-col justify-between"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-3">
              <div>
                <h4 className="font-bold text-sm text-gray-900 flex items-center gap-1.5">
                  <span>สลิปแจ้งเตือน</span>
                  <span className="font-mono text-blue-600 font-bold">
                    #{selectedSlipModal.shortId || selectedSlipModal.orderId?.slice(-6)}
                  </span>
                </h4>
                <p className="text-[10px] text-gray-500">
                  {selectedSlipModal.customerName} ({selectedSlipModal.phone || "-"})
                </p>
              </div>
              <button
                onClick={() => setSelectedSlipModal(null)}
                className="p-1 rounded-md hover:bg-gray-100 text-gray-500 transition"
              >
                <FiX className="text-lg" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto flex items-center justify-center bg-gray-50 rounded-md p-2 border border-gray-100 mb-3">
              <img
                src={selectedSlipModal.slipUrl}
                alt="Slip Preview"
                className="max-h-[55vh] object-contain rounded-md shadow-sm"
              />
            </div>

            <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-100 text-xs">
              <button
                onClick={() => window.open(selectedSlipModal.slipUrl, "_blank")}
                className="text-gray-600 hover:text-gray-900 font-semibold text-[11px] underline"
              >
                เปิดรูปขนาดเต็ม
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    window.open(`/orders/${selectedSlipModal.orderId}`, "_blank");
                    setSelectedSlipModal(null);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-3 py-1.5 rounded-md text-xs transition shadow-2xs flex items-center gap-1"
                >
                  <FiExternalLink />
                  <span>ไปที่คำสั่งซื้อ</span>
                </button>
                <button
                  onClick={() => setSelectedSlipModal(null)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold px-3 py-1.5 rounded-md text-xs transition"
                >
                  ปิด
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Quick Order Slip Preview Modal ── */}
      {quickOrderSlip && (
        <div
          className="fixed inset-0 z-50 bg-black/75 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setQuickOrderSlip(null)}
        >
          <div
            className="bg-white rounded-md p-4 max-w-md w-full shadow-2xl border border-gray-200 max-h-[90vh] flex flex-col justify-between"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-3">
              <div>
                <h4 className="font-bold text-sm text-gray-900 flex items-center gap-1.5">
                  <span>สลิปคำสั่งซื้อรออนุมัติ</span>
                  <span className="font-mono text-blue-600 font-bold">
                    #{quickOrderSlip.shortId || quickOrderSlip.id?.slice(-6)}
                  </span>
                </h4>
                <p className="text-[10px] text-gray-500">
                  {quickOrderSlip.customerName} ({quickOrderSlip.phone || "-"}) • ยอด: ฿{Number(quickOrderSlip.totalPrice).toLocaleString()} ({quickOrderSlip.size} ใบ)
                </p>
              </div>
              <button
                onClick={() => setQuickOrderSlip(null)}
                className="p-1 rounded-md hover:bg-gray-100 text-gray-500 transition"
              >
                <FiX className="text-lg" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto flex items-center justify-center bg-gray-50 rounded-md p-2 border border-gray-100 mb-3">
              <img
                src={quickOrderSlip.slipUrl}
                alt="Order Slip Preview"
                className="max-h-[55vh] object-contain rounded-md shadow-sm"
              />
            </div>

            <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-100 text-xs">
              <button
                onClick={() => window.open(quickOrderSlip.slipUrl, "_blank")}
                className="text-gray-600 hover:text-gray-900 font-semibold text-[11px] underline"
              >
                เปิดรูปขนาดเต็ม
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    window.open(`/orders/${quickOrderSlip.id}`, "_blank");
                    setQuickOrderSlip(null);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-3 py-1.5 rounded-md text-xs transition shadow-2xs flex items-center gap-1"
                >
                  <FiExternalLink />
                  <span>เปิดหน้าคำสั่งซื้อ</span>
                </button>
                <button
                  onClick={() => setQuickOrderSlip(null)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold px-3 py-1.5 rounded-md text-xs transition"
                >
                  ปิด
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── Realtime Order Top-Right Floating Toast Notifications (Matching /orders style) ── */}
      {orderNotifications.length > 0 && (
        <div className="fixed top-16 right-4 z-50 flex flex-col gap-2.5 max-w-sm w-full pointer-events-none animate-slideInRight">
          {orderNotifications.map((noti) => {
            const isPaid = noti.type === "PAID";
            const bgClass = isPaid
              ? "bg-[#4DA1A9] text-white"
              : noti.type === "APPROVE"
                ? "bg-[rgb(59,176,101)] text-white"
                : noti.type === "DANGER"
                  ? "bg-[#c15b78] text-white"
                  : "bg-[#FAB12F] text-white";

            return (
              <div
                key={noti.id}
                className={`pointer-events-auto p-3.5 rounded-md shadow-lg transition-all duration-300 ${bgClass}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h5 className="text-sm font-bold leading-tight">
                      {noti.title}: {noti.customerName || "ลูกค้าทั่วไป"}
                    </h5>
                  </div>
                  <button
                    onClick={() => dismissNotification(noti.id)}
                    className="p-1 text-white/80 hover:text-white hover:bg-black/10 rounded transition"
                  >
                    <FiX className="text-sm" />
                  </button>
                </div>

                <div className="text-xs text-white/95 font-medium mb-2.5">
                  <span>
                    {noti.size} ใบ —{" "}
                    {noti.currency === "lak" && noti.amountLak
                      ? `₭${Number(noti.amountLak).toLocaleString()}`
                      : `${Number(noti.totalPrice || 0).toLocaleString()} บาท`}
                  </span>{" "}
                  <span className="font-mono text-white/80 font-bold">(#{noti.shortId || noti.orderId?.slice(-6)})</span>
                </div>

                {/* Action buttons inside toast */}
                <div className="flex items-center gap-1.5 pt-1.5 border-t border-white/20 text-xs">
                  {noti.slipUrl && (
                    <button
                      onClick={() => {
                        setQuickOrderSlip({
                          id: noti.orderId,
                          shortId: noti.shortId,
                          slipUrl: noti.slipUrl,
                          customerName: noti.customerName,
                          phone: noti.phone,
                          totalPrice: noti.totalPrice,
                          size: noti.size,
                        });
                        dismissNotification(noti.id);
                      }}
                      className="flex-1 bg-white/20 hover:bg-white/30 text-white font-bold py-1 px-2.5 rounded-md border border-white/30 transition text-[11px] flex items-center justify-center gap-1 shadow-2xs"
                    >
                      <FiEye />
                      <span>ดูสลิป</span>
                    </button>
                  )}
                  <button
                    onClick={() => {
                      window.open(`/orders/${noti.orderId}`, "_blank");
                      dismissNotification(noti.id);
                    }}
                    className="flex-1 bg-white text-gray-900 hover:bg-white/90 font-bold py-1 px-2.5 rounded-md transition text-[11px] flex items-center justify-center gap-1 shadow-2xs"
                  >
                    <FiExternalLink />
                    <span>เปิดออเดอร์</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}


      {/* Modal ตรวจสอบคำขอย้ายคำสั่งซื้อ */}
      {selectedTransferModal && (
        <TransferReviewModal
          isOpen={Boolean(selectedTransferModal)}
          onClose={() => setSelectedTransferModal(null)}
          request={selectedTransferModal}
          onSuccess={() => {
            setSelectedTransferModal(null);
          }}
        />
      )}

      {/* Modal ดำเนินการเซนเซอร์ข้อมูลส่วนบุคคล (Batch Anonymize) */}
      <BatchAnonymizeModal
        isOpen={isBatchModalOpen}
        onClose={() => {
          setIsBatchModalOpen(false);
          fetchPendingAnonymizeSummary();
        }}
        totalPending={pendingAnonymizeCount}
        onSuccess={() => {
          fetchPendingAnonymizeSummary();
        }}
      />
    </div>
  );
};



export default AdminCenter;
